import styled, { keyframes } from 'styled-components'

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: 24px;
  overflow-y: auto;
  font-family: 'Montserrat', sans-serif;
  color: #ffffff;
`

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  .title-group {
    h2 {
      font-size: 24px;
      font-weight: 700;
      color: ${(props) => props.theme.titleColor};
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 10px;

      svg {
        color: #3b82f6;
      }
    }
    p {
      font-size: 13px;
      color: ${(props) => props.theme.subtitleColor};
    }
  }

  .actions-group {
    display: flex;
    align-items: center;
    gap: 12px;
  }
`

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`

export const StatCard = styled.div<{ $variant?: 'blue' | 'green' | 'red' | 'purple' }>`
  background: ${props => {
    switch (props.$variant) {
      case 'red':
        return 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(185, 28, 28, 0.05) 100%)'
      case 'green':
        return 'linear-gradient(135deg, rgba(34, 197, 94, 0.12) 0%, rgba(21, 128, 61, 0.05) 100%)'
      case 'purple':
        return 'linear-gradient(135deg, rgba(168, 85, 247, 0.12) 0%, rgba(126, 34, 206, 0.05) 100%)'
      case 'blue':
      default:
        return 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(29, 78, 216, 0.05) 100%)'
    }
  }};
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'red':
        return 'rgba(239, 68, 68, 0.25)'
      case 'green':
        return 'rgba(34, 197, 94, 0.25)'
      case 'purple':
        return 'rgba(168, 85, 247, 0.25)'
      case 'blue':
      default:
        return 'rgba(59, 130, 246, 0.25)'
    }
  }};
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);

  .icon-box {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    background: ${props => {
      switch (props.$variant) {
        case 'red':
          return 'rgba(239, 68, 68, 0.2)'
        case 'green':
          return 'rgba(34, 197, 94, 0.2)'
        case 'purple':
          return 'rgba(168, 85, 247, 0.2)'
        case 'blue':
        default:
          return 'rgba(59, 130, 246, 0.2)'
      }
    }};
    color: ${props => {
      switch (props.$variant) {
        case 'red':
          return '#f87171'
        case 'green':
          return '#4ade80'
        case 'purple':
          return '#c084fc'
        case 'blue':
        default:
          return '#60a5fa'
      }
    }};
  }

  .stat-info {
    h4 {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: ${props => props.theme.name === 'light' ? '#475569' : '#94a3b8'};
      margin-bottom: 4px;
    }
    .value {
      font-size: 26px;
      font-weight: 800;
      color: ${props => props.theme.name === 'light' ? '#0f172a' : '#ffffff'};
      line-height: 1;
    }
  }
`

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  .tabs {
    display: flex;
    gap: 8px;
    background: ${props => props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.03)'};
    padding: 4px;
    border-radius: 12px;
    border: 1px solid ${props => props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.06)'};
  }

  .filter-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
`

export const TabButton = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? '#2563eb' : 'transparent'};
  color: ${props => props.$active ? '#ffffff' : props.theme.name === 'light' ? '#475569' : '#94a3b8'};
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Montserrat', sans-serif;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    color: ${props => props.theme.name === 'light' ? '#0f172a' : '#ffffff'};
    background: ${props => props.$active ? '#2563eb' : props.theme.name === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)'};
  }

  .badge-count {
    background: ${props => props.$active ? 'rgba(255, 255, 255, 0.2)' : props.theme.name === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'};
    padding: 2px 6px;
    border-radius: 6px;
    font-size: 11px;
  }
`

export const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${props => props.theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.04)'};
  border: 1px solid ${props => props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 10px;
  padding: 8px 14px;
  min-width: 260px;
  box-shadow: ${props => props.theme.name === 'light' ? '0 2px 8px rgba(0, 31, 92, 0.04)' : 'none'};

  svg {
    color: ${props => props.theme.name === 'light' ? '#64748b' : '#94a3b8'};
  }

  input {
    background: transparent;
    border: none;
    outline: none;
    color: ${props => props.theme.textPrimary};
    font-size: 13px;
    width: 100%;
    font-family: 'Montserrat', sans-serif;

    &::placeholder {
      color: ${props => props.theme.name === 'light' ? '#94a3b8' : '#64748b'};
    }
  }
`

export const SelectBox = styled.select`
  height: 38px;
  border-radius: 10px;
  border: 1px solid ${props => props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.08)'};
  background: ${props => props.theme.name === 'light' ? '#ffffff' : '#001842'};
  color: ${props => props.theme.textPrimary};
  padding: 0 12px;
  font-size: 13px;
  font-family: 'Montserrat', sans-serif;
  outline: none;
  cursor: pointer;
  box-shadow: ${props => props.theme.name === 'light' ? '0 2px 8px rgba(0, 31, 92, 0.04)' : 'none'};

  option {
    background: ${props => props.theme.name === 'light' ? '#ffffff' : '#001842'};
    color: ${props => props.theme.name === 'light' ? '#0f172a' : '#ffffff'};
  }
`

export const RefreshButton = styled.button`
  background: ${props => props.theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.theme.name === 'light' ? '#0f172a' : '#ffffff'};
  border-radius: 10px;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.1)'};
    color: #60a5fa;
  }

  .spinning {
    animation: ${spin} 1s linear infinite;
  }
`

export const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.cardBorder};
  border-radius: 16px;
  margin-bottom: 16px;
  box-shadow: ${props => props.theme.name === 'light' ? '0 4px 20px rgba(0, 31, 92, 0.06)' : 'none'};

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
  }
`

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 13px;

  th {
    background: ${props => props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.03)'};
    padding: 14px 16px;
    color: ${props => props.theme.name === 'light' ? '#334155' : '#94a3b8'};
    font-weight: 700;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.5px;
    border-bottom: 1px solid ${props => props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)'};
    position: sticky;
    top: 0;
    z-index: 10;
  }

  td {
    padding: 14px 16px;
    border-bottom: 1px solid ${props => props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.04)'};
    color: ${props => props.theme.textPrimary};
    white-space: nowrap;
    font-weight: 500;
  }

  tr:hover td {
    background: ${props => props.theme.name === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.02)'};
  }
`

export const StatusBadge = styled.span<{ $status: string }>`
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  background: ${props => {
    switch (props.$status?.toUpperCase()) {
      case 'SUCESSO':
        return 'rgba(34, 197, 94, 0.15)'
      case 'REPROCESSADO':
        return 'rgba(59, 130, 246, 0.15)'
      case 'ERRO':
      default:
        return 'rgba(239, 68, 68, 0.15)'
    }
  }};

  color: ${props => {
    switch (props.$status?.toUpperCase()) {
      case 'SUCESSO':
        return '#4ade80'
      case 'REPROCESSADO':
        return '#60a5fa'
      case 'ERRO':
      default:
        return '#f87171'
    }
  }};

  border: 1px solid ${props => {
    switch (props.$status?.toUpperCase()) {
      case 'SUCESSO':
        return 'rgba(34, 197, 94, 0.3)'
      case 'REPROCESSADO':
        return 'rgba(59, 130, 246, 0.3)'
      case 'ERRO':
      default:
        return 'rgba(239, 68, 68, 0.3)'
    }
  }};
`

export const TypeBadge = styled.span<{ $tipo: string }>`
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  background: ${props => props.$tipo === 'ESTOQUE' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(99, 102, 241, 0.15)'};
  color: ${props => props.$tipo === 'ESTOQUE' ? '#fbbf24' : '#818cf8'};
  border: 1px solid ${props => props.$tipo === 'ESTOQUE' ? 'rgba(245, 158, 11, 0.3)' : 'rgba(99, 102, 241, 0.3)'};
`

export const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' | 'ghost' }>`
  background: ${props => {
    switch (props.$variant) {
      case 'primary':
        return '#2563eb'
      case 'danger':
        return 'rgba(239, 68, 68, 0.2)'
      case 'ghost':
      default:
        return 'rgba(255, 255, 255, 0.05)'
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'primary':
        return '#ffffff'
      case 'danger':
        return '#f87171'
      case 'ghost':
      default:
        return '#e2e8f0'
    }
  }};
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'primary':
        return '#3b82f6'
      case 'danger':
        return 'rgba(239, 68, 68, 0.4)'
      case 'ghost':
      default:
        return 'rgba(255, 255, 255, 0.1)'
    }
  }};
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  font-family: 'Montserrat', sans-serif;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spinning {
    animation: ${spin} 1s linear infinite;
  }
`

export const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 13px;
  color: #94a3b8;

  .page-controls {
    display: flex;
    gap: 6px;
  }
`

export const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`

export const ModalContent = styled.div`
  background: #001842;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  max-width: 650px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 90vh;
  overflow-y: auto;

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding-bottom: 12px;

    h3 {
      font-size: 18px;
      font-weight: 700;
      color: #ffffff;
      display: flex;
      align-items: center;
      gap: 10px;

      svg {
        color: #f87171;
      }
    }

    button {
      background: transparent;
      border: none;
      color: #94a3b8;
      font-size: 20px;
      cursor: pointer;
      &:hover {
        color: #ffffff;
      }
    }
  }

  .meta-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    background: rgba(255, 255, 255, 0.02);
    padding: 12px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.05);

    .item {
      span.label {
        font-size: 11px;
        color: #64748b;
        text-transform: uppercase;
        display: block;
      }
      span.val {
        font-size: 13px;
        color: #e2e8f0;
        font-weight: 600;
        word-break: break-all;
      }
    }
  }

  .error-block {
    background: rgba(239, 68, 68, 0.08);
    border: 1px solid rgba(239, 68, 68, 0.2);
    border-radius: 12px;
    padding: 16px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 12px;
    color: #fca5a5;
    white-space: pre-wrap;
    word-break: break-all;
    max-height: 250px;
    overflow-y: auto;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    padding-top: 16px;
  }
`
